import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ExternalLink, Video, FileText } from "lucide-react";
import { API_URL } from "../../config";
import { SPECIALTIES } from "../../SpecialtyContext";

const PAGE = 25;
const TYPES = ["Лекция", "Мастер-класс", "Вебинар", "Материал"];
const STATUS_LABEL = { new: "Новое", published: "Опубликовано", hidden: "Скрыто" };

const fmtDate = s => (s ? new Date(s.endsWith("Z") ? s : `${s}Z`).toLocaleDateString("ru-RU") : "—");
const fmtDateTime = s => (s ? new Date(s.endsWith("Z") ? s : `${s}Z`).toLocaleString("ru-RU") : "—");

export default function VkTab({ token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [list, setList] = useState({ total: 0, items: [] });
  const [kind, setKind] = useState("");
  const [state, setState] = useState("new");
  const [q, setQ] = useState("");
  const [hasAuthor, setHasAuthor] = useState("");     // "" | "true" | "false"
  const [flt, setFlt] = useState({ topic: "", minLen: "", maxLen: "", yearFrom: "", yearTo: "", hasVideo: "", hasImages: "" });
  const [postSpec, setPostSpec] = useState("arrhythmology");   // раздел для статей из постов без своей подсказки
  const [preview, setPreview] = useState(null);                // { action, matched, sample }
  const [page, setPage] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");
  const [picked, setPicked] = useState({});          // { [id]: true }
  const [choice, setChoice] = useState({});          // { [id]: { specialty, material_type } }
  const [author, setAuthor] = useState(() => { try { return localStorage.getItem("vk_default_author") || ""; } catch { return ""; } });
  const qTimer = useRef(null);
  const [allSpec, setAllSpec] = useState("");        // раздел для «опубликовать все новые видео»

  // условия подборки постов (одни и те же для списка и для массовых действий)
  const filterParams = () => {
    const out = [];
    if (flt.topic) out.push(["topic", flt.topic]);
    if (flt.minLen !== "") out.push(["min_len", flt.minLen]);
    if (flt.maxLen !== "") out.push(["max_len", flt.maxLen]);
    if (flt.yearFrom !== "") out.push(["year_from", flt.yearFrom]);
    if (flt.yearTo !== "") out.push(["year_to", flt.yearTo]);
    if (flt.hasVideo) out.push(["has_video", flt.hasVideo]);
    if (flt.hasImages) out.push(["has_images", flt.hasImages]);
    return out;
  };
  const bulkBody = (action, extra = {}) => {
    const b = { action, kind: "post", q: q.trim() || null, ...extra };
    filterParams().forEach(([k, v]) => { b[k] = ["has_video", "has_images"].includes(k) ? v === "true" : (k === "topic" ? v : Number(v)); });
    return b;
  };
  const PRESETS = [
    ["Поздравления", { topic: "greetings" }], ["Анонсы мероприятий", { topic: "announcements" }], ["Книги и рубрики", { topic: "books" }],
    ["Клинические разборы", { topic: "cases" }], ["Короткие (до 300)", { maxLen: "300" }], ["Содержательные (от 1000)", { minLen: "1000" }],
    ["Старше 2020", { yearTo: "2020" }], ["С видео сообщества", { hasVideo: "true" }],
  ];
  const applyPreset = patch => { setFlt({ topic: "", minLen: "", maxLen: "", yearFrom: "", yearTo: "", hasVideo: "", hasImages: "", ...patch }); setPage(0); setPreview(null); };

  const bulkPreview = async action => {
    const r = await fetch(`${API_URL}/admin/vk/bulk`, { method: "POST", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify(bulkBody(action, { dry_run: true })) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || "Не удалось посчитать"); return; }
    setPreview({ action, matched: d.matched, sample: d.sample });
  };
  const bulkRun = async () => {
    const { action, matched } = preview;
    const label = { hide: "Скрыть", unhide: "Вернуть из скрытых", publish: "Опубликовать как статьи" }[action];
    if (action === "publish" && !postSpec) { setMsg("Выберите раздел для статей."); return; }
    if (!window.confirm(`${label}: ${matched} постов. Продолжить?`)) return;
    let done = 0, guard = 0;
    setMsg("Выполняем…");
    for (;;) {
      const r = await fetch(`${API_URL}/admin/vk/bulk`, { method: "POST", headers: { "Content-Type": "application/json", ...auth },
        body: JSON.stringify(bulkBody(action, { dry_run: false, specialty: postSpec, author: author.trim() || null, limit: 50 })) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg(d.detail || "Не удалось выполнить"); break; }
      done += d.changed ?? d.published ?? 0;
      if (action !== "publish" || !d.remaining || !(d.published > 0) || ++guard > 40) { setMsg(`Готово: ${done}.${d.skipped_no_specialty ? ` Пропущено без раздела: ${d.skipped_no_specialty}.` : ""}`); break; }
      setMsg(`Опубликовано ${done} из ${matched}…`);
    }
    setPreview(null); loadStatus(); loadList();
  };

  const loadStatus = useCallback(() => {
    fetch(`${API_URL}/admin/vk/status`, { headers: auth }).then(r => (r.ok ? r.json() : null)).then(setStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadList = useCallback(() => {
    const p = new URLSearchParams({ limit: PAGE, offset: page * PAGE });
    if (kind) p.set("kind", kind);
    if (state) p.set("status", state);
    if (q.trim()) p.set("q", q.trim());
    if (hasAuthor) p.set("has_author", hasAuthor);
    if (kind === "post") filterParams().forEach(([k, v]) => p.set(k, v));
    fetch(`${API_URL}/admin/vk/items?${p}`, { headers: auth })
      .then(r => (r.ok ? r.json() : { total: 0, items: [] }))
      .then(data => {
        setList(data);
        setChoice(prev => {
          const next = { ...prev };
          data.items.forEach(i => {
            if (!next[i.id]) next[i.id] = { specialty: i.suggested_specialty || "", material_type: i.suggested_material_type || "Материал" };
          });
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, kind, state, q, hasAuthor, flt, page]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(loadList, 300);
    return () => clearTimeout(qTimer.current);
  }, [loadList]);

  const saveDefaultAuthor = v => { setAuthor(v); try { localStorage.setItem("vk_default_author", v); } catch { /* noop */ } };

  const sync = async () => {
    setSyncing(true); setMsg("");
    try {
      const r = await fetch(`${API_URL}/admin/vk/sync`, { method: "POST", headers: auth });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Не удалось синхронизировать");
      setMsg(`Готово: видео ${d.videos}, постов ${d.posts}; новых ${d.added}, обновлено ${d.updated}.`);
      setPage(0); loadStatus(); loadList();
    } catch (e) { setMsg(e.message); loadStatus(); }
    finally { setSyncing(false); }
  };

  const setStatusOf = async (item, next) => {
    await fetch(`${API_URL}/admin/vk/items/${item.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify({ status: next }),
    });
    loadStatus(); loadList();
  };

  const publish = async ids => {
    const items = ids.map(id => ({ id, specialty: choice[id]?.specialty || "", material_type: choice[id]?.material_type, author: author.trim() || null }));
    if (items.some(i => !i.specialty)) { setMsg("Выберите специальность у каждой записи, которую публикуете."); return; }
    const r = await fetch(`${API_URL}/admin/vk/publish`, {
      method: "POST", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify({ items }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || "Не удалось опубликовать"); return; }
    const skipped = (d.skipped || []).map(s => `№${s.id}: ${s.reason}`).join("; ");
    setMsg(`Опубликовано: ${d.published.length}.${skipped ? ` Пропущено: ${skipped}` : ""}`);
    setPicked({}); loadStatus(); loadList();
  };

  const publishAllVideos = async () => {
    if (!allSpec) { setMsg("Выберите раздел, в который публикуем все новые видео."); return; }
    const label = SPECIALTIES.find(x => x.id === allSpec)?.label;
    const count = status?.counts?.video_new || 0;
    if (!window.confirm(`Опубликовать все новые видео (${count}) в раздел «${label}»?
Автор: ${author.trim() || "не указан"}.
Потом любое можно снять с публикации кнопкой в списке.`)) return;
    setMsg("Публикуем…");
    const r = await fetch(`${API_URL}/admin/vk/publish-all`, {
      method: "POST", headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ specialty: allSpec, author: author.trim() || null, q: q.trim() || null }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || "Не удалось опубликовать"); return; }
    setMsg(`Опубликовано видео: ${d.published}${d.linked_existing ? `, уже были на сайте (связаны без дубля): ${d.linked_existing}` : ""}.`);
    loadStatus(); loadList();
  };

  const saveAuthor = async (item, value) => {
    if ((value || "").trim() === (item.author || "")) return;
    await fetch(`${API_URL}/admin/vk/items/${item.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify({ author: value }),
    });
    loadList();
  };

  const unpublish = async item => {
    if (!window.confirm(`Снять с публикации «${item.title}»? Материал на сайте будет удалён, запись вернётся в «Новые».`)) return;
    const r = await fetch(`${API_URL}/admin/vk/items/${item.id}/unpublish`, { method: "POST", headers: auth });
    const d = await r.json().catch(() => ({}));
    setMsg(r.ok ? "Снято с публикации." : (d.detail || "Не удалось снять с публикации"));
    loadStatus(); loadList();
  };

  const pickedIds = Object.keys(picked).filter(k => picked[k]).map(Number);
  const pages = Math.max(1, Math.ceil(list.total / PAGE));
  const c = status?.counts || {};
  const n = (k, s) => c[`${k}_${s}`] || 0;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <strong>Сообщество ВКонтакте</strong>{" "}
            <a href={`https://vk.com/club${status?.group_id || ""}`} target="_blank" rel="noopener noreferrer">club{status?.group_id}</a>
            <div style={{ color: "#888", fontSize: "0.85rem", marginTop: 4 }}>
              {status?.configured ? "Ключ настроен" : "Ключ не настроен"} · последняя синхронизация: {fmtDateTime(status?.last_sync_at)}
              {" · "}видео: {n("video", "new")} новых / {n("video", "published")} на сайте · постов: {n("post", "new")} новых / {n("post", "published")} на сайте
            </div>
            {status?.last_error && <div style={{ color: "#c0392b", fontSize: "0.85rem", marginTop: 4 }}>Последняя ошибка: {status.last_error}</div>}
          </div>
          <button type="button" onClick={sync} disabled={syncing || !status?.configured} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={15} className={syncing ? "spin" : ""} /> {syncing ? "Синхронизация…" : "Синхронизировать сейчас"}
          </button>
        </div>
        {status && !status.configured && (
          <p style={{ margin: "12px 0 0", color: "#9c0040", fontSize: "0.9rem" }}>
            Интеграция не настроена. Администратор сообщества создаёт ключ доступа (Управление → Работа с API → Ключи доступа, права «видео» и «стена»),
            а в файл <code>/opt/medplatform/.env</code> на сервере добавляется строка <code>VK_API_TOKEN=…</code>, после чего перезапускается бэкенд.
          </p>
        )}
        {status?.configured && <p style={{ margin: "10px 0 0", color: "#888", fontSize: "0.82rem" }}>Новое из сообщества подтягивается само раз в 12 часов; записи остаются «на складе», пока вы их не опубликуете.</p>}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <select className="admin-input" style={{ width: "auto" }} value={kind} onChange={e => { setKind(e.target.value); setPage(0); }}>
          <option value="">Видео и посты</option><option value="video">Только видео</option><option value="post">Только посты</option>
        </select>
        <select className="admin-input" style={{ width: "auto" }} value={state} onChange={e => { setState(e.target.value); setPage(0); }}>
          <option value="new">Новые</option><option value="published">Опубликованные</option><option value="hidden">Скрытые</option><option value="">Все</option>
        </select>
        <input className="admin-input" style={{ width: 240 }} placeholder="Поиск по названию и тексту" value={q} onChange={e => { setQ(e.target.value); setPage(0); }} />
        <select className="admin-input" style={{ width: "auto" }} value={hasAuthor} onChange={e => { setHasAuthor(e.target.value); setPage(0); }}>
          <option value="">Докладчик: любой</option><option value="true">Докладчик найден</option><option value="false">Докладчик не найден</option>
        </select>
        <input className="admin-input" style={{ width: 260 }} placeholder="Один автор для ВСЕХ (необязательно)" title="Если заполнено, перекрывает докладчиков у записей. Оставьте пустым, чтобы у каждой записи был свой автор (или профиль центра)." value={author} onChange={e => saveDefaultAuthor(e.target.value)} />
      </div>

      {kind === "post" && (
        <div className="card" style={{ marginBottom: 12 }}>
          <strong>Подборка постов</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0" }}>
            {PRESETS.map(([label, patch]) => (
              <button type="button" key={label} onClick={() => applyPreset(patch)} style={{ padding: "4px 11px", fontSize: "0.82rem", background: "var(--gray-100)", color: "var(--gray-800)", border: "1px solid var(--gray-300)" }}>{label}</button>
            ))}
            <button type="button" onClick={() => applyPreset({})} style={{ padding: "4px 11px", fontSize: "0.82rem", background: "none", color: "var(--blue)" }}>сбросить</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <input className="admin-input" style={{ width: 110 }} type="number" placeholder="длина от" value={flt.minLen} onChange={e => { setFlt({ ...flt, minLen: e.target.value }); setPage(0); setPreview(null); }} />
            <input className="admin-input" style={{ width: 110 }} type="number" placeholder="длина до" value={flt.maxLen} onChange={e => { setFlt({ ...flt, maxLen: e.target.value }); setPage(0); setPreview(null); }} />
            <input className="admin-input" style={{ width: 100 }} type="number" placeholder="год от" value={flt.yearFrom} onChange={e => { setFlt({ ...flt, yearFrom: e.target.value }); setPage(0); setPreview(null); }} />
            <input className="admin-input" style={{ width: 100 }} type="number" placeholder="год до" value={flt.yearTo} onChange={e => { setFlt({ ...flt, yearTo: e.target.value }); setPage(0); setPreview(null); }} />
            <select className="admin-input" style={{ width: "auto" }} value={flt.hasImages} onChange={e => { setFlt({ ...flt, hasImages: e.target.value }); setPage(0); setPreview(null); }}>
              <option value="">фото: любые</option><option value="true">с фото</option><option value="false">без фото</option>
            </select>
            <span style={{ color: "#888", fontSize: "0.85rem" }}>Найдено в списке: <strong>{list.total}</strong> (статус «{state ? STATUS_LABEL[state] : "все"}»)</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <button type="button" onClick={() => bulkPreview("hide")} style={{ padding: "5px 12px", background: "var(--gray-500)" }}>Скрыть найденные…</button>
            <button type="button" onClick={() => bulkPreview("unhide")} style={{ padding: "5px 12px", background: "var(--gray-500)" }}>Вернуть скрытые найденные…</button>
            <select className="admin-input" style={{ width: "auto" }} value={postSpec} onChange={e => setPostSpec(e.target.value)} title="Раздел для постов, у которых нет собственной подсказки специальности">
              {SPECIALTIES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
            <button type="button" onClick={() => bulkPreview("publish")} style={{ padding: "5px 12px" }}>Опубликовать найденные как статьи…</button>
          </div>
          {preview && (
            <div style={{ marginTop: 12, padding: 12, background: "#fff1f6", border: "1.5px solid #ffc2d9", borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: "#9c0040" }}>
                {{ hide: "Будет скрыто", unhide: "Будет возвращено", publish: "Будет опубликовано как статьи" }[preview.action]}: {preview.matched}
              </div>
              {preview.sample.length > 0 && <ul style={{ margin: "6px 0", paddingLeft: 18, color: "#666", fontSize: "0.85rem" }}>{preview.sample.map(t => <li key={t}>{t.slice(0, 90)}</li>)}</ul>}
              <button type="button" disabled={!preview.matched} onClick={bulkRun} style={{ padding: "5px 14px" }}>Подтвердить</button>{" "}
              <button type="button" onClick={() => setPreview(null)} style={{ padding: "5px 14px", background: "var(--gray-500)" }}>Отмена</button>
              {preview.action === "publish" && <div style={{ color: "#888", fontSize: "0.8rem", marginTop: 6 }}>Статьи публикуются порциями по 50; раздел берётся из подсказки поста, а если её нет — выбранный выше. Автор: центр «Аритмология» (или поле «Один автор для ВСЕХ»).</div>}
            </div>
          )}
        </div>
      )}

      {status?.configured && n("video", "new") > 0 && (
        <div className="card" style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <strong>Опубликовать все новые видео ({n("video", "new")}) в раздел:</strong>
          <select className="admin-input" style={{ width: "auto" }} value={allSpec} onChange={e => setAllSpec(e.target.value)}>
            <option value="">— выберите раздел —</option>
            {SPECIALTIES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
          <button type="button" onClick={publishAllVideos}>Опубликовать все</button>
          <span style={{ color: "#888", fontSize: "0.82rem" }}>Автор — докладчик из записи (подсказку можно поправить у каждого видео) либо центр «Аритмология»; профили авторов, которых ещё нет на сайте, создаются сами. Тип определяется по названию. Поиск ниже сужает набор.</span>
        </div>
      )}

      {msg && <div className="card" style={{ marginBottom: 12, background: "#fff1f6", border: "1.5px solid #ffc2d9", color: "#9c0040" }}>{msg}</div>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <span style={{ color: "#888", fontSize: "0.88rem" }}>Найдено: {list.total}</span>
        {state === "new" && list.items.length > 0 && (
          <>
            <button type="button" onClick={() => setPicked(Object.fromEntries(list.items.map(i => [i.id, true])))} style={{ padding: "4px 10px", background: "var(--gray-500)" }}>Выбрать все на странице</button>
            {pickedIds.length > 0 && (
              <>
                <select className="admin-input" style={{ width: "auto" }} value="" onChange={e => { const v = e.target.value; if (v) setChoice(c2 => { const nx = { ...c2 }; pickedIds.forEach(id => { nx[id] = { ...nx[id], specialty: v }; }); return nx; }); }}>
                  <option value="">Задать специальность выбранным…</option>
                  {SPECIALTIES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
                <button type="button" onClick={() => publish(pickedIds)} style={{ padding: "4px 12px" }}>Опубликовать выбранные ({pickedIds.length})</button>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {list.items.map(i => (
          <div key={i.id} className="card" style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            {i.status === "new" && (
              <input type="checkbox" checked={!!picked[i.id]} onChange={e => setPicked(p => ({ ...p, [i.id]: e.target.checked }))} aria-label="Выбрать" style={{ marginTop: 6 }} />
            )}
            {i.thumb_url
              ? <img src={i.thumb_url} alt="" loading="lazy" referrerPolicy="no-referrer" style={{ width: 150, height: 90, objectFit: "cover", borderRadius: 8, flexShrink: 0, background: "var(--gray-100)" }} />
              : <div style={{ width: 150, height: 90, borderRadius: 8, background: "var(--gray-100)", flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.75rem", fontWeight: 700, color: "var(--blue)" }}>
                  {i.kind === "video" ? <Video size={13} /> : <FileText size={13} />} {i.kind === "video" ? "Видео" : "Пост"}
                </span>
                <span style={{ color: "#888", fontSize: "0.8rem" }}>
                  {fmtDate(i.published_at)}{i.duration ? ` · ${i.duration}` : ""} · просмотров {i.views}{i.kind === "post" && i.image_count ? ` · фото ${i.image_count}` : ""}
                </span>
                {i.status !== "new" && <span style={{ fontSize: "0.75rem", fontWeight: 700, color: i.status === "published" ? "#15803d" : "#888" }}>{STATUS_LABEL[i.status]}</span>}
              </div>
              <div style={{ fontWeight: 700 }}>{i.title}</div>
              {i.text && <div style={{ color: "#666", fontSize: "0.85rem", marginTop: 4, maxHeight: 54, overflow: "hidden" }}>{i.text.slice(0, 260)}</div>}
              {i.note && <div style={{ color: "#9c0040", fontSize: "0.8rem", marginTop: 4 }}>{i.note}</div>}
              {i.kind === "video" && i.status === "new" && (
                <div style={{ marginTop: 8 }}>
                  <input className="admin-input" style={{ maxWidth: 420 }} key={`${i.id}-${i.author || ""}`}
                    defaultValue={i.author || ""} placeholder="Докладчик (ФИО) — если пусто, автор: центр «Аритмология»"
                    onBlur={e => saveAuthor(i, e.target.value)} />
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
                {i.status === "new" && (
                  <>
                    <select className="admin-input" style={{ width: "auto" }} value={choice[i.id]?.specialty || ""} onChange={e => setChoice(c2 => ({ ...c2, [i.id]: { ...c2[i.id], specialty: e.target.value } }))}>
                      <option value="">— специальность —</option>
                      {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    {i.kind === "video" && (
                      <select className="admin-input" style={{ width: "auto" }} value={choice[i.id]?.material_type || "Материал"} onChange={e => setChoice(c2 => ({ ...c2, [i.id]: { ...c2[i.id], material_type: e.target.value } }))}>
                        {TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    )}
                    <button type="button" onClick={() => publish([i.id])} disabled={i.kind === "video" && !i.has_embed} style={{ padding: "5px 12px" }}>Опубликовать</button>
                    <button type="button" onClick={() => setStatusOf(i, "hidden")} style={{ padding: "5px 12px", background: "var(--gray-500)" }}>Скрыть</button>
                  </>
                )}
                {i.status === "hidden" && <button type="button" onClick={() => setStatusOf(i, "new")} style={{ padding: "5px 12px" }}>Вернуть в новые</button>}
                {i.status === "published" && i.published_id && (
                  <>
                    <Link to={`/${i.published_as === "lecture" ? "lectures" : "articles"}/${i.published_id}`}>Открыть на сайте →</Link>
                    <button type="button" onClick={() => unpublish(i)} style={{ padding: "4px 10px", background: "var(--gray-500)" }}>Снять с публикации</button>
                  </>
                )}
                {i.source_url && <a href={i.source_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}>во ВК <ExternalLink size={12} /></a>}
              </div>
            </div>
          </div>
        ))}
        {list.items.length === 0 && <p style={{ color: "#888" }}>{status?.configured ? "Здесь пока пусто — нажмите «Синхронизировать сейчас» или смените фильтр." : "Записей нет: интеграция ещё не настроена."}</p>}
      </div>

      {pages > 1 && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "center", marginTop: 16 }}>
          <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: "5px 12px" }}>← Назад</button>
          <span style={{ color: "#888" }}>{page + 1} / {pages}</span>
          <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)} style={{ padding: "5px 12px" }}>Дальше →</button>
        </div>
      )}
    </div>
  );
}
